import { EM_DASH } from "@/utils/chars";

export const DEGREE = "°";

// Number of minutes a humidistat maximum runtime of `0` represents on CMW.
export const DEFAULT_HUMIDISTAT_RUNTIME_MINUTES = 30;

/**
 * Formats an "enabled + threshold value" pair into a single display string,
 * e.g. `Enabled (50°)`, `Disabled (42°)`, or `Enabled (80%)`.
 *
 * When both the flag and value are missing we render EM_DASH. When only the
 * value is missing we render just the enabled/disabled state.
 */
export function formatEnabledWithValue(
  enabled: boolean | null | undefined,
  value: number | null | undefined,
  unit: string = DEGREE
): string {
  if (enabled === null || enabled === undefined) {
    return value === null || value === undefined ? EM_DASH : `${value}${unit}`;
  }

  const state = enabled ? "Enabled" : "Disabled";

  if (value === null || value === undefined) {
    return state;
  }

  return `${state} (${value}${unit})`;
}

/**
 * Formats a stored setpoint value with a degree symbol, falling back to EM_DASH
 * when the value is missing. Does not convert between temperature scales.
 */
export function formatSetpoint(
  value: number | null | undefined,
  unit: string = DEGREE
): string {
  if (value === null || value === undefined) {
    return EM_DASH;
  }

  return `${value}${unit}`;
}

/**
 * Formats the humidistat maximum runtime. A stored value of `0` means the
 * CMW default (30 min) is used. Missing values render EM_DASH.
 *
 * The runtime only applies when humidistat (Safety Humidity) control is
 * enabled. When it is explicitly disabled we render "Disabled" so a stored
 * `0` is not mistaken for an active default runtime.
 */
export function formatHumidistatRuntime(
  minutes: number | null | undefined,
  enabled?: boolean | null
): string {
  if (enabled === false) {
    return "Disabled";
  }

  if (minutes === null || minutes === undefined) {
    return EM_DASH;
  }

  if (minutes === 0) {
    return `Default (${DEFAULT_HUMIDISTAT_RUNTIME_MINUTES} min)`;
  }

  return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
}
